import { Check } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useState, type FC } from 'react';

import Result from 'components/v2/common/Result';

import type { Application } from 'api';
import useTranslate from 'hooks/useTranslate';

interface AttachToCaseDialogProps {
    open: boolean;
    documentLabel: string;
    cases: Application[];
    defaultCaseId: number | null;
    loading?: boolean;
    onClose: () => void;
    onConfirm: (applicationId: number) => void;
}

const AttachToCaseDialog: FC<AttachToCaseDialogProps> = ({
    open,
    documentLabel,
    cases,
    defaultCaseId,
    loading = false,
    onClose,
    onConfirm,
}) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedCaseId, setSelectedCaseId] = useState<number | null>(defaultCaseId);

    useEffect(() => {
        if (open) {
            setSelectedCaseId(defaultCaseId);
        }
    }, [defaultCaseId, open]);

    const handleClose = () => {
        if (loading) return;
        onClose();
    };

    return (
        <Dialog open={open} fullWidth maxWidth="sm" fullScreen={isMobile} scroll="paper">
            <DialogTitle>
                {translate('attachToCase')} - {documentLabel}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {translate('attachToCaseSubtitle')}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: 'rgba(248, 250, 252, 0.55)' }}>
                {cases.length === 0 ? (
                    <Result
                        title={translate('noCasesYet')}
                        description={translate('noCasesYetDescription')}
                        sx={{ py: 3 }}
                    />
                ) : (
                    <List disablePadding>
                        {cases.map((app) => {
                            const selected = selectedCaseId === app.id;
                            return (
                                <ListItemButton
                                    key={app.id}
                                    selected={selected}
                                    disabled={loading}
                                    onClick={() => setSelectedCaseId(app.id)}
                                    sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        border: '1px solid',
                                        borderColor: selected ? 'primary.main' : 'divider',
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: selected ? 600 : 500 }}
                                            >
                                                {app.program_name}
                                            </Typography>
                                        }
                                    />
                                    {selected ? (
                                        <ListItemIcon sx={{ minWidth: 28, color: 'primary.main' }}>
                                            <Check fontSize="small" />
                                        </ListItemIcon>
                                    ) : null}
                                </ListItemButton>
                            );
                        })}
                    </List>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button variant="outlined" onClick={handleClose} disabled={loading}>
                    {translate('cancel')}
                </Button>
                <Button
                    variant="contained"
                    disabled={loading || !selectedCaseId || cases.length === 0}
                    onClick={() => {
                        if (selectedCaseId) onConfirm(selectedCaseId);
                    }}
                >
                    {loading ? translate('copying') : translate('confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AttachToCaseDialog;
